import type { AuthorBioBlockContent, AuthorEntry } from "../types"

interface AuthorBioBlockEditorProps {
  content: AuthorBioBlockContent | null
  onChange: (content: AuthorBioBlockContent) => void
}

const INPUT: React.CSSProperties = {
  width: "100%",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 8,
  padding: "7px 10px",
  color: "#fff",
  fontSize: 12,
  fontFamily: "'DM Sans', sans-serif",
  outline: "none",
  boxSizing: "border-box" as const,
}

const LABEL: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  color: "rgba(255,255,255,0.4)",
  fontFamily: "'DM Sans', sans-serif",
  marginBottom: 4,
  display: "block",
}

const SECTION_LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: "rgba(255,255,255,0.6)",
  fontFamily: "'DM Sans', sans-serif",
  marginBottom: 8,
}

function emptyAuthor(): AuthorEntry {
  return { name: "", role: "", bio: "", image_url: undefined, social_links: [] }
}

export function AuthorBioBlockEditor({
  content,
  onChange,
}: AuthorBioBlockEditorProps) {
  const c = content ?? {}
  const authors: AuthorEntry[] = c.authors && c.authors.length > 0
    ? c.authors
    : c.name // legacy single-author migration
      ? [{ name: c.name, role: c.role, bio: c.bio, image_url: c.image_url, social_links: c.social_links }]
      : [emptyAuthor()]

  const update = (partial: Partial<AuthorBioBlockContent>) =>
    onChange({ ...c, ...partial })

  const updateAuthor = (idx: number, partial: Partial<AuthorEntry>) => {
    const next = authors.map((a, i) => (i === idx ? { ...a, ...partial } : a))
    update({ authors: next })
  }

  const addAuthor = () => update({ authors: [...authors, emptyAuthor()] })

  const removeAuthor = (idx: number) =>
    update({ authors: authors.filter((_, i) => i !== idx) })

  const setLink = (authorIdx: number, linkIdx: number, field: "label" | "url", val: string) => {
    const links = [...(authors[authorIdx].social_links ?? [])]
    links[linkIdx] = { ...links[linkIdx], [field]: val }
    updateAuthor(authorIdx, { social_links: links })
  }

  const addLink = (authorIdx: number) => {
    const links = [...(authors[authorIdx].social_links ?? []), { label: "", url: "" }]
    updateAuthor(authorIdx, { social_links: links })
  }

  const removeLink = (authorIdx: number, linkIdx: number) => {
    const links = (authors[authorIdx].social_links ?? []).filter((_, i) => i !== linkIdx)
    updateAuthor(authorIdx, { social_links: links })
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Section heading */}
      <div>
        <span style={LABEL}>Section heading</span>
        <input
          type="text"
          value={c.heading ?? "About the Author"}
          onChange={(e) => update({ heading: e.target.value })}
          placeholder="About the Author"
          style={INPUT}
        />
      </div>

      {/* Author cards */}
      {authors.map((author, idx) => (
        <div
          key={idx}
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 10,
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={SECTION_LABEL}>
              {authors.length > 1 ? `Author ${idx + 1}` : "Author"}
            </span>
            {authors.length > 1 && (
              <button
                type="button"
                onClick={() => removeAuthor(idx)}
                style={{
                  background: "none",
                  border: "none",
                  color: "rgba(255,255,255,0.3)",
                  fontSize: 11,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  padding: "2px 6px",
                }}
              >
                Remove
              </button>
            )}
          </div>

          <div>
            <span style={LABEL}>Name</span>
            <input
              type="text"
              value={author.name ?? ""}
              onChange={(e) => updateAuthor(idx, { name: e.target.value })}
              placeholder="Author name"
              style={INPUT}
            />
          </div>

          <div>
            <span style={LABEL}>Title / Role</span>
            <input
              type="text"
              value={author.role ?? ""}
              onChange={(e) => updateAuthor(idx, { role: e.target.value })}
              placeholder="e.g. Executive Director"
              style={INPUT}
            />
          </div>

          <div>
            <span style={LABEL}>Bio</span>
            <textarea
              value={author.bio ?? ""}
              onChange={(e) => updateAuthor(idx, { bio: e.target.value })}
              placeholder="Short bio…"
              rows={3}
              style={{ ...INPUT, resize: "vertical" as const, minHeight: 60 }}
            />
          </div>

          <div>
            <span style={LABEL}>Photo URL</span>
            <input
              type="text"
              value={author.image_url ?? ""}
              onChange={(e) => updateAuthor(idx, { image_url: e.target.value || undefined })}
              placeholder="https://…"
              style={INPUT}
            />
          </div>

          {/* Social links */}
          <div>
            <span style={LABEL}>Social links</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {(author.social_links ?? []).map((link, li) => (
                <div key={li} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input
                    type="text"
                    placeholder="Label"
                    value={link.label ?? ""}
                    onChange={(e) => setLink(idx, li, "label", e.target.value)}
                    style={{ ...INPUT, flex: 1 }}
                  />
                  <input
                    type="text"
                    placeholder="URL"
                    value={link.url ?? ""}
                    onChange={(e) => setLink(idx, li, "url", e.target.value)}
                    style={{ ...INPUT, flex: 2 }}
                  />
                  <button
                    type="button"
                    onClick={() => removeLink(idx, li)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "rgba(255,255,255,0.3)",
                      fontSize: 14,
                      cursor: "pointer",
                      padding: "0 4px",
                      flexShrink: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addLink(idx)}
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px dashed rgba(255,255,255,0.12)",
                  borderRadius: 7,
                  color: "rgba(255,255,255,0.4)",
                  fontSize: 11,
                  cursor: "pointer",
                  padding: "5px 10px",
                  fontFamily: "'DM Sans', sans-serif",
                  alignSelf: "flex-start",
                }}
              >
                + Add link
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Add author */}
      <button
        type="button"
        onClick={addAuthor}
        style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px dashed rgba(255,255,255,0.15)",
          borderRadius: 8,
          color: "rgba(255,255,255,0.5)",
          fontSize: 12,
          cursor: "pointer",
          padding: "8px 14px",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        + Add author / contributor
      </button>
    </div>
  )
}
